// CAD drawing import for the polygon editor: DXF text → layers of ready-made polygons.
//
// Parse (HATCH, POLYLINE, blocks, arcs, code pages) → pick the unit → recentre (georeferenced
// drawings sit millions of units from the origin, beyond float32 precision) → per layer, polygons
// from every source: hatches (with holes), closed shapes, open lines chained into rings and, for
// layers drawn only with lines, the faces those lines enclose → classify the layer (road, sidewalk,
// building, green) from its name. Small repeated blocks (trees, lamps) are symbols, not areas.
import { DXFData, DXFEntity } from '../../new-project/dxf/types';
import { LayerClass, classifyLayer } from './classifyLayer';
import { Pt, bbox, signedArea } from './geometry';
import { DXFDoc2, Shape, UNIT_TO_M, decodeDxfBytes, parseDXF2 } from './parseDxf';
import { chainClose, polygonizeFaces } from './polygonize';

interface ImportedPolygon {
  points: Pt[];
  holes?: Pt[][];
}

export interface ImportedLayer {
  name: string;
  cls: LayerClass;
  /** Off or frozen in the drawing. */
  hidden: boolean;
  polygons: ImportedPolygon[];
}

export interface CadImport {
  /** The drawing's lines, recentred, shown under the polygons. */
  reference: DXFData;
  layers: ImportedLayer[];
  metersPerUnit: number;
  units: { chosen: string; header: string; reason: string };
  /** Drawing coordinates of the imported origin (subtracted from everything). */
  offset: Pt;
  /** Existing luminaires: blocks on lighting layers, recentred. */
  lights: { layer: string; x: number; y: number }[];
  stats: { entities: number; polygons: number; parseMs: number; polygonizeMs: number };
}

const UNIT_NAMES: Record<number, string> = {
  0: 'unitless',
  1: 'in',
  2: 'ft',
  4: 'mm',
  5: 'cm',
  6: 'm',
  7: 'km',
  21: 'usft',
};

/**
 * `$INSUNITS` is often wrong in GIS exports (inches for metre data). Keep the header unit when the
 * site and its typical building are a plausible size in it, otherwise take the first plausible one.
 */
export function guessUnits(
  doc: Pick<DXFDoc2, 'insunits' | 'measurement'>,
  extent: [number, number, number, number],
  buildingAreas: number[],
): { metersPerUnit: number; chosen: string; reason: string } {
  const [x0, y0, x1, y1] = extent;
  const diag = Math.hypot(x1 - x0, y1 - y0);
  const maxAbs = Math.max(Math.abs(x0), Math.abs(x1), Math.abs(y0), Math.abs(y1));
  const sorted = [...buildingAreas].sort((a, b) => a - b);
  const medianBuilding = sorted.length ? sorted[Math.floor(sorted.length / 2)] : NaN;
  const candidates: [string, number][] = [
    ['m', 1],
    ['mm', 0.001],
    ['cm', 0.01],
    ['ft', 0.3048],
    ['in', 0.0254],
  ];
  const plausible = (f: number) => {
    const site = diag * f;
    const building = medianBuilding * f * f;
    return site >= 30 && site <= 30000 && (isNaN(medianBuilding) || (building >= 15 && building <= 30000));
  };
  const header = UNIT_TO_M[doc.insunits];
  // Georeferenced (UTM, Gauss-Krüger, State Plane): coordinates in the hundreds of thousands.
  const projected = maxAbs > 1e5;
  if (header && plausible(header) && !(projected && [1, 4, 5].includes(doc.insunits))) {
    return { metersPerUnit: header, chosen: UNIT_NAMES[doc.insunits] ?? String(doc.insunits), reason: 'header' };
  }
  const imperialFirst = doc.measurement === 0 && !projected;
  const order = imperialFirst ? [candidates[3], candidates[4], ...candidates.slice(0, 3)] : candidates;
  const hit = order.find(([, f]) => plausible(f));
  if (hit) return { metersPerUnit: hit[1], chosen: hit[0], reason: projected ? 'projected' : 'plausible size' };
  return { metersPerUnit: 1, chosen: 'm', reason: 'default' };
}

/** Rough identity of a ring (centre and size), to drop the same area found by two sources. */
function ringKey(ring: Pt[], cell: number): string {
  let sx = 0;
  let sy = 0;
  for (const p of ring) {
    sx += p[0];
    sy += p[1];
  }
  const area = Math.abs(signedArea(ring));
  return `${Math.round(sx / ring.length / cell)},${Math.round(sy / ring.length / cell)},${Math.round(Math.log(area + 1e-9) * 10)}`;
}

const shift = (points: Pt[], [ox, oy]: Pt): Pt[] => points.map(([x, y]) => [x - ox, y - oy]);

/** Imports a DXF file (raw bytes: the code page is read from the file). */
export function importDxf(bytes: Uint8Array): CadImport {
  const t0 = Date.now();
  const doc = parseDXF2(decodeDxfBytes(bytes).text);
  const parseMs = Date.now() - t0;

  const hatchPatterns: Record<string, Set<string>> = {};
  for (const s of doc.entities) if (s.hatchPattern) (hatchPatterns[s.layer] ??= new Set()).add(s.hatchPattern);
  const classes: Record<string, LayerClass> = {};
  for (const layer of doc.layers) {
    classes[layer] = classifyLayer(layer, { hatchPatterns: [...(hatchPatterns[layer] ?? [])] });
  }

  const allPoints = doc.entities.flatMap((s) => s.vertices);
  const extent = allPoints.length ? bbox(allPoints) : ([0, 0, 0, 0] as [number, number, number, number]);
  const buildingAreas = doc.entities
    .filter((s) => s.closed && !s.viaInsert && classes[s.layer]?.type === 'Building')
    .map((s) => Math.abs(signedArea(s.vertices)));
  const units = guessUnits(doc, extent, buildingAreas);
  const offset: Pt = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

  const perMetre = 1 / units.metersPerUnit;
  const snap = 0.01 * perMetre; // 1 cm: real CAD endpoints meet exactly
  const minArea = 0.5 * perMetre * perMetre; // 0.5 m²

  // Small repeated blocks (trees, lamps, signs) are symbols: their shapes are not areas.
  const instanceBox = new Map<number, [number, number, number, number]>();
  for (const s of doc.entities) {
    if (s.insertId === undefined) continue;
    const b = bbox(s.vertices);
    const cur = instanceBox.get(s.insertId);
    instanceBox.set(
      s.insertId,
      cur ? [Math.min(cur[0], b[0]), Math.min(cur[1], b[1]), Math.max(cur[2], b[2]), Math.max(cur[3], b[3])] : b,
    );
  }
  const symbolMax = Math.min(3 * perMetre, Math.hypot(extent[2] - extent[0], extent[3] - extent[1]) * 0.01);
  const symbols = new Set<number>();
  for (const [id, b] of instanceBox) if (Math.hypot(b[2] - b[0], b[3] - b[1]) < symbolMax) symbols.add(id);

  const byLayer = new Map<string, Shape[]>();
  for (const s of doc.entities) {
    const list = byLayer.get(s.layer);
    if (list) list.push(s);
    else byLayer.set(s.layer, [s]);
  }

  const t1 = Date.now();
  const layers: ImportedLayer[] = doc.layers.map((name) => {
    const cls = classes[name];
    const info = doc.layerInfo[name];
    const layer: ImportedLayer = { name, cls, hidden: !!(info?.off || info?.frozen), polygons: [] };
    if (cls.role === 'ignore' || cls.role === 'lighting') return layer;

    const seen = new Set<string>();
    const cell = Math.max(snap * 10, 0.05 * perMetre);
    const add = (points: Pt[], holes?: Pt[][]) => {
      if (points.length < 3 || Math.abs(signedArea(points)) < minArea) return;
      const key = ringKey(points, cell);
      if (seen.has(key)) return;
      seen.add(key);
      layer.polygons.push({
        points: shift(points, offset),
        ...(holes?.length ? { holes: holes.map((h) => shift(h, offset)) } : {}),
      });
    };

    const open: Pt[][] = [];
    for (const s of byLayer.get(name) ?? []) {
      if (s.insertId !== undefined && symbols.has(s.insertId)) continue;
      if (s.type === 'HATCH') add(s.vertices, s.holes);
      else if (s.closed) add(s.vertices);
      else open.push(s.vertices);
    }
    if (open.length) {
      const areasBefore = layer.polygons.length;
      for (const ring of chainClose(open, snap).rings) add(ring);
      // Faces between lines only where the layer draws its areas with lines alone: on layers
      // with hatches or closed outlines, lines are hatching or detail and would cut areas into strips.
      if (areasBefore === 0) {
        for (const face of polygonizeFaces(open, snap, minArea).faces) add(face.outer, face.holes);
      }
    }
    return layer;
  });
  const polygonizeMs = Date.now() - t1;

  const lightingLayers = new Set(layers.filter((l) => l.cls.role === 'lighting').map((l) => l.name));
  const entities: DXFEntity[] = doc.entities
    .filter((s) => s.vertices.length >= 2)
    .map((s) => ({
      type: s.type,
      layer: s.layer,
      vertices: shift(s.vertices, offset),
      closed: s.closed,
      radius: 0,
      startAngle: 0,
      endAngle: 0,
      center: [0, 0],
    }));

  return {
    reference: { layers: doc.layers, entities },
    layers,
    metersPerUnit: units.metersPerUnit,
    units: {
      chosen: units.chosen,
      header: UNIT_NAMES[doc.insunits] ?? String(doc.insunits),
      reason: units.reason,
    },
    offset,
    lights: doc.inserts
      .filter((i) => lightingLayers.has(i.layer))
      .map((i) => ({ layer: i.layer, x: i.x - offset[0], y: i.y - offset[1] })),
    stats: {
      entities: doc.entities.length,
      polygons: layers.reduce((n, l) => n + l.polygons.length, 0),
      parseMs,
      polygonizeMs,
    },
  };
}

/**
 * Layers for a drawing read by another parser (SVG): its closed shapes are the polygons, and the
 * layer names are classified the same way.
 */
export function importParsedDrawing(data: DXFData): CadImport {
  const byLayer = new Map<string, ImportedPolygon[]>();
  for (const e of data.entities) {
    if (!e.closed || e.vertices.length < 3) continue;
    const list = byLayer.get(e.layer);
    if (list) list.push({ points: e.vertices });
    else byLayer.set(e.layer, [{ points: e.vertices }]);
  }
  const layers = data.layers.map((name) => ({
    name,
    cls: classifyLayer(name),
    hidden: false,
    polygons: byLayer.get(name) ?? [],
  }));
  return {
    reference: data,
    layers,
    metersPerUnit: 1,
    units: { chosen: 'm', header: 'unitless', reason: 'default' },
    offset: [0, 0],
    lights: [],
    stats: {
      entities: data.entities.length,
      polygons: layers.reduce((n, l) => n + l.polygons.length, 0),
      parseMs: 0,
      polygonizeMs: 0,
    },
  };
}
